/* global React, BIMScene, BIM_STAGES, StageBracket */
const { useState, useEffect, useRef } = React;

/* Navbar moved to js/Navbar.jsx (it's shared across every page). */

function Hero() {
  // BIMScene.jsx exposes window.BIM_STAGES once it loads. Fall back to
  // DZ_SERVICES.stages if BIMScene hasn't initialised yet, or if WebGL
  // is disabled and BIMScene never runs at all.
  const STAGES = (window.BIM_STAGES) ||
    (window.DZ_SERVICES && window.DZ_SERVICES.stages
      ? window.DZ_SERVICES.stages.map(s => ({
          id: s.id, label: s.label, slug: s.slug,
          services: s.services || []
        }))
      : []);
  // Merge services from DZ_SERVICES into BIM_STAGES (which lacks them)
  // so the mobile story-mode cards can show a stage's 3 linked services.
  const dzStagesData = (window.DZ_SERVICES && window.DZ_SERVICES.stages) || [];
  STAGES.forEach(s => {
    if (!s.services) {
      const dz = dzStagesData.find(d => d.id === s.id);
      if (dz) { s.services = dz.services || []; s.slug = dz.slug; }
    }
  });
  const data = window.DZ_HOME.hero;
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  // webglOk = false: render the per-stage <video> (DEFAULT — the videos are
  //                  recordings of the same BIMScene WebGL render and are
  //                  pixel-identical, but they always work).
  //         = true:  render the live <BIMScene> (Three.js). Only enabled
  //                  via `?webgl=1` URL flag, since some enterprise Chrome
  //                  builds let BIMScene mount without errors but render a
  //                  blank canvas — leaving the visitor with an empty hero.
  const [webglOk, setWebglOk] = useState(
    typeof window !== 'undefined' &&
    window.location.search.includes('webgl=1')
  );
  const videoRef = useRef(null);
  const advanceTimerRef = useRef(null);
  // Reading window — after each video clip ends in chain mode we pause
  // on the last frame for this many ms so the visitor can read the
  // service pills + stage card content before the next clip plays.
  // Mobile uses a snappier cadence — smaller screen, less to read,
  // visitors thumb through faster on phones (and the full 11-stage
  // cycle becomes ~38s instead of ~57s, which feels less laggy).
  const isMobileViewport = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 700px)').matches;
  const ADVANCE_INTERVAL_MS = () => (isMobileViewport() ? 3500 : 5200);
  const READING_WINDOW_MS   = () => (isMobileViewport() ? 1500 : 3500);
  const readingTimerRef = useRef(null);
  // Chain mode = true: advance to next stage on timer (WebGL path) or
  //   on `ended` event (video path).
  // Chain mode = false: stay on the current stage until the user clicks
  //   another stage or hits restart.
  const chainModeRef = useRef(true);

  // ─── Per-stage videos (fallback path only) ──────────────────────────
  // Sliced from assets/hero-video/bim-lifecycle.mp4 with ffmpeg, one
  // ~2.2-s clip per stage. Used when webglOk === false.
  //
  // Mobile (≤800px viewport): use the -mobile.mp4 variants encoded at
  // 800px wide, 12-28 KB each (vs. 100-264 KB at desktop res). Saves
  // ~2.2 MB per page load on phones. Mobile detection is synchronous
  // at component init so the picked URL is the one passed to <video src>
  // on first paint (matters for LCP).
  const isMobile = typeof window !== 'undefined' &&
                   window.matchMedia &&
                   window.matchMedia('(max-width: 800px)').matches;
  const STAGE_VIDEOS_DESKTOP = [
    '/assets/hero-video/stages/stage-01-programming.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-02-conceptual-design.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-03-detailed-design.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-04-analysis.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-05-documentation.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-06-fabrication.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-07-4d-5d.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-08-construction.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-09-operation-maintenance.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-10-renovation.mp4?v=2026-05-06rich2',
    '/assets/hero-video/stages/stage-11-demolition.mp4?v=2026-05-06rich2'
  ];
  const STAGE_VIDEOS_MOBILE = STAGE_VIDEOS_DESKTOP.map(
    (u) => u.replace('.mp4?', '-mobile.mp4?')
  );
  const STAGE_VIDEOS = isMobile ? STAGE_VIDEOS_MOBILE : STAGE_VIDEOS_DESKTOP;

  // ─── WebGL detection ─────────────────────────────────────────────────
  // Cheap synchronous check on mount: try to create a context, render a
  // red pixel, read it back. If any step fails, fall back to the video.
  // Also honours `?nogpu=1` URL param for manual testing.
  useEffect(() => {
    if (typeof window !== 'undefined' &&
        window.location.search.includes('nogpu=1')) {
      setWebglOk(false);
      return;
    }
    let usable = true;
    try {
      const c = document.createElement('canvas');
      c.width = 4; c.height = 4;
      const gl = c.getContext('webgl2') ||
                 c.getContext('webgl') ||
                 c.getContext('experimental-webgl');
      if (!gl) {
        usable = false;
      } else {
        const dbg = gl.getExtension('WEBGL_debug_renderer_info');
        if (dbg) {
          const renderer = String(
            gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || ''
          ).toLowerCase();
          if (renderer.includes('swiftshader') ||
              renderer.includes('software') ||
              renderer.includes('llvmpipe')) {
            usable = false;
          }
        }
        if (usable) {
          gl.clearColor(1, 0, 0, 1);
          gl.clear(gl.COLOR_BUFFER_BIT);
          const px = new Uint8Array(4);
          gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
          if (px[0] < 200 || px[1] > 40 || px[2] > 40) usable = false;
          if (px[0] === 0 && px[1] === 0 &&
              px[2] === 0 && px[3] === 0) usable = false;
        }
      }
    } catch (e) {
      usable = false;
    }
    if (!usable) setWebglOk(false);
  }, []);

  // ─── Auto-advance timer (drives the chain through all 11 stages) ───
  // Runs in BOTH render modes:
  //   WebGL: setActive triggers BIMScene's own animated camera move;
  //          interval 5.2 s gives each stage time to settle.
  //   Video: setActive swaps the <video src>, but in WebGL-disabled
  //          mode we let the <video onEnded> handler advance instead
  //          (clip length ≈ 2.2 s feels too snappy on a 5.2 s timer).
  useEffect(() => {
    clearInterval(advanceTimerRef.current);
    if (!playing || !webglOk) return;  // video path uses onEnded
    if (!chainModeRef.current) return; // user locked to a stage
    advanceTimerRef.current = setInterval(() => {
      if (chainModeRef.current) {
        setActive(prev => (prev + 1) % STAGES.length);
      }
    }, ADVANCE_INTERVAL_MS());
    return () => clearInterval(advanceTimerRef.current);
  }, [playing, webglOk, STAGES.length, active]);

  // ─── Video src sync (fallback path only) ───────────────────────────
  useEffect(() => {
    if (webglOk) return;
    const v = videoRef.current;
    if (!v) return;
    const target = STAGE_VIDEOS[active];
    const cur = v.src || '';
    if (!cur.endsWith(target)) {
      v.src = target;
      v.load();
    }
    if (playing) {
      const onCanPlay = () => v.play().catch(() => {});
      if (v.readyState >= 2) onCanPlay();
      else v.addEventListener('loadeddata', onCanPlay, { once: true });
      return () => v.removeEventListener('loadeddata', onCanPlay);
    } else {
      try { v.pause(); } catch (_) {}
    }
  }, [active, playing, webglOk]);

  // ─── Video onEnded (fallback path only) ────────────────────────────
  // Each per-stage clip is ~2.2 s. When the chain is engaged, the
  // ended event walks us to the next stage. When locked, it loops
  // the same clip.
  const onEnded = () => {
    const v = videoRef.current;
    if (!v) return;
    // Always freeze on the last frame first — gives the visitor a
    // moment with the model paused and the services panel readable.
    try { v.pause(); } catch (_) {}
    setLocked(true);   // turns on the soft aqua pulse around the model
    if (chainModeRef.current) {
      // Reading window before auto-advancing to the next stage.
      // User taps + restart cancel this timer.
      clearTimeout(readingTimerRef.current);
      readingTimerRef.current = setTimeout(() => {
        if (!chainModeRef.current) return;
        setLocked(false);   // pulse off — new clip is about to play
        setActive(prev => (prev + 1) % STAGES.length);
      }, READING_WINDOW_MS());
    }
    // Else (user-locked): no timer, video stays paused indefinitely.
  };

  // Locked state — true whenever the user has tapped a stage to lock
  // it (chain mode is paused). Drives the .is-locked CSS class on the
  // scene container so the video pulses softly when paused on the
  // last frame, indicating the currently active stage.
  const [locked, setLocked] = useState(false);

  // ─── Mobile-only: keep the active card centered when active changes
  // (auto-advance OR arrow taps). On desktop the timeline is a static
  // grid so no scroll is needed.
  //
  // BUG FIX (2026-05-10): the previous implementation used
  //   activeCard.scrollIntoView({ block: 'nearest', inline: 'center' })
  // which on auto-advance also pulled the WINDOW scroll back up to
  // the hero whenever the user had scrolled past it — every 3.5s the
  // page would yank itself back to the top. block:'nearest' is meant
  // to skip vertical scroll but Chrome and iOS Safari still trigger
  // a scroll-into-view ancestor walk that catches the window.
  //
  // Fix: scroll ONLY the carousel container's horizontal offset.
  // carousel.scrollTo({ left: ... }) cannot affect window scroll by
  // definition — it operates on the carousel's own scroll position.
  useEffect(() => {
    if (!isMobileViewport()) return;
    const carousel = document.querySelector('.dz-lifecycle__steps');
    const activeCard = carousel && carousel.querySelector('.dz-step--active');
    if (!carousel || !activeCard) return;
    // Centre the active card within the carousel:
    // target scrollLeft = card's left within carousel, minus half the
    // empty space on either side. With flex:0 0 100% cards (which we
    // have), this reduces to simply `activeCard.offsetLeft` because
    // card width == carousel client width.
    const targetScrollLeft = activeCard.offsetLeft -
      Math.max(0, (carousel.clientWidth - activeCard.offsetWidth) / 2);
    carousel.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  }, [active]);

  // ─── User actions ──────────────────────────────────────────────────
  const jumpTo = (i) => {
    chainModeRef.current = false;
    clearTimeout(readingTimerRef.current);
    setLocked(false);   // play the new stage's clip first; lock turns
                        // back on automatically when the clip ends
    setActive(i);
    setPlaying(true);
  };
  const togglePlay = () => {
    const next = !playing;
    setPlaying(next);
    const v = videoRef.current;
    if (v) {
      try {
        if (next) v.play().catch(() => {});
        else v.pause();
      } catch (_) {}
    }
  };
  const restart = () => {
    chainModeRef.current = true;
    clearTimeout(readingTimerRef.current);
    setLocked(false);
    setActive(0);
    setPlaying(true);
    const v = videoRef.current;
    if (v) {
      try { v.currentTime = 0; v.play().catch(() => {}); } catch (_) {}
    }
  };

  // Mobile-only nav arrows. Wraps both directions so visitors at
  // either end of the 11-stage cycle can keep navigating. Locks the
  // chain like jumpTo — visitor can re-engage auto-advance with the
  // play/restart buttons.
  const goPrev = () => {
    chainModeRef.current = false;
    clearTimeout(readingTimerRef.current);
    setLocked(false);
    setActive((prev) => (prev - 1 + STAGES.length) % STAGES.length);
  };
  const goNext = () => {
    chainModeRef.current = false;
    clearTimeout(readingTimerRef.current);
    setLocked(false);
    setActive((prev) => (prev + 1) % STAGES.length);
  };

  const current = STAGES[active] || { label: '' };

  return (
    <section className="dz-hero" id="dz-main" aria-labelledby="hero-title">
      {webglOk ? (
        /* ── WebGL primary: live Three.js BIMScene ─────────────────── */
        <div className={'dz-hero__scene' + (locked ? ' is-locked' : '')}>
          {typeof BIMScene === 'function' && (
            <BIMScene activeStage={active} onStageChange={setActive} />
          )}
        </div>
      ) : (
        /* ── Fallback: per-stage MP4 ──────────────────────────────── */
        <div className={'dz-hero__scene dz-hero__scene--video' + (locked ? ' is-locked' : '')}>
          <video
            ref={videoRef}
            className="dz-hero__video"
            src={STAGE_VIDEOS[0]}
            poster="/assets/hero-video/poster-mobile.jpg"
            muted
            autoPlay
            playsInline
            preload="auto"
            fetchpriority="high"
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload nofullscreen noremoteplayback"
            onEnded={onEnded}
            aria-label="BIM lifecycle visualization"
          />
        </div>
      )}
      <div className="dz-hero__vignette" aria-hidden="true" />
      <div className="dz-hero__pattern" aria-hidden="true" />

      {/* ── Services-per-stage bracket callout ── */}
      <StageBracket stageIndex={active} stageLabel={current.label} />

      <div className="dz-hero__content">
        <div className="dz-hero__eyebrow">
          <span className="dz-hero__eyebrow-dot" />
          <span className="dz-caption">{data.eyebrow}</span>
        </div>

        <h1 id="hero-title" className="dz-hero__title">
          {data.titleLeading} <span className="dz-hero__title-accent">{data.titleAccent}</span>{data.titlePunct ?? ','}<br/>
          {data.titleTrailing}
        </h1>

        <p className="dz-hero__lead">{data.lead}</p>

        <div className="dz-hero__ctas">
          <a href={data.ctas.primary.href} className="dz-btn dz-btn--aqua">
            {data.ctas.primary.label}
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M3 8h9M8 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
          <a href={data.ctas.secondary.href} className="dz-btn dz-btn--ghost">{data.ctas.secondary.label}</a>
        </div>
      </div>

      {/* ── Lifecycle timeline UI ── */}
      <div className="dz-lifecycle" aria-label="BIM lifecycle timeline">
        <div className="dz-lifecycle__head">
          <div>
            <span className="dz-caption dz-lifecycle__eyebrow">
              {data.lifecycle.stagePrefix} {String(active + 1).padStart(2,'0')} / {String(STAGES.length).padStart(2,'0')}
            </span>
            <div className="dz-lifecycle__current" key={active}>
              {current.label}
            </div>
          </div>
          <div className="dz-lifecycle__controls">
            <button className="dz-lifecycle__btn" onClick={restart} aria-label={data.lifecycle.restartLabel}>
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path d="M2 7a5 5 0 1 1 1.46 3.54" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <path d="M2 3v4h4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button className="dz-lifecycle__btn dz-lifecycle__btn--primary" onClick={togglePlay} aria-label={playing ? data.lifecycle.pauseLabel : data.lifecycle.playLabel}>
              {playing
                ? <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><rect x="2" y="1.5" width="2.5" height="9" fill="currentColor"/><rect x="7.5" y="1.5" width="2.5" height="9" fill="currentColor"/></svg>
                : <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 1.5 L10 6 L2.5 10.5 Z" fill="currentColor"/></svg>
              }
            </button>
          </div>
        </div>

        {/* Mobile-only stage navigation. Visible only at ≤700px via
            CSS — desktop has the timeline-grid below for navigation.
            Both arrows lock chain mode (auto-advance pauses); visitor
            re-engages by tapping a stage in the cards or scrolling
            back to start. */}
        <div className="dz-lifecycle__nav-mobile" aria-label="Stage navigation">
          <button className="dz-lifecycle__nav-btn" onClick={goPrev} aria-label="Previous stage" type="button">
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path d="M11 3 6 9l5 6" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {/* Stage identifier sits BETWEEN the two arrows. Number
              + name combo replaces the previous "01 / 11" counter
              because the user gets stage identity here instead of
              the now-hidden big "01 / Programming" labels inside
              the card. aria-live keeps screen-readers in sync as
              the user taps prev/next or auto-advance ticks. */}
          {/* Stage name on top (visual focus) with a faint "Stage N"
              ghost subtitle below — replaces the previous "#NN ·
              Name" inline format. Number is gone from the visible
              counter; the ghost text serves as a quiet position
              indicator without competing with the stage name or
              with LOD numbers in service rows below. */}
          <span className="dz-lifecycle__nav-meta" aria-live="polite">
            <span className="dz-lifecycle__nav-name">{current.label}</span>
            <span className="dz-lifecycle__nav-ghost" aria-hidden="true">
              Stage {active + 1}
            </span>
          </span>
          <button className="dz-lifecycle__nav-btn" onClick={goNext} aria-label="Next stage" type="button">
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path d="M7 3l5 6-5 6" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <ol className="dz-lifecycle__steps">
          {STAGES.map((s, i) => {
            const state = i < active ? 'done' : i === active ? 'active' : 'upcoming';
            return (
              <li key={s.id} className={`dz-step dz-step--${state}`}>
                <button
                  className="dz-step__btn"
                  onClick={() => jumpTo(i)}
                  aria-current={i === active ? 'step' : undefined}
                >
                  <span className="dz-step__num">{String(i + 1).padStart(2,'0')}</span>
                  <span className="dz-step__label">{s.label}</span>
                  <span className="dz-step__bar" aria-hidden="true"/>
                </button>
                {/* Mobile-only: services list shown inside the story-mode card.
                    Hidden on desktop via CSS (.dz-step__services { display: none }). */}
                {s.services && s.services.length > 0 && (
                  <ul className="dz-step__services" aria-label={`${s.label} services`}>
                    {s.services.map((sv) => (
                      <li key={sv.slug}>
                        <a
                          className="dz-step__service"
                          href={`${window.location.pathname.startsWith('/ar/') ? '/ar' : ''}/services/${s.slug}/#service-${sv.slug}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>{sv.name}</span>
                          <span className="dz-step__service-arrow" aria-hidden="true">→</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

window.Hero = Hero;
